import { Component, Suspense, type ReactNode } from 'react';
import { Html, Text, useTexture } from '@react-three/drei';
import type { ThreeEvent } from '@react-three/fiber';
import type { EventTwinItem } from '@cdf/api-client-react';

interface WallPlaqueMeshProps {
  item: EventTwinItem;
  selected: boolean;
  wallTangentOffset?: number;
  onSelect: (item: EventTwinItem) => void;
}

interface PlaqueVisualProps {
  item: EventTwinItem;
  selected: boolean;
  position: [number, number, number];
  onSelect: (item: EventTwinItem) => void;
}

interface PlaqueTextureBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

const PLAQUE_WIDTH = 2.25;
const PLAQUE_HEIGHT = 1.5;
const WALL_OFFSET = 0.07;

class PlaqueTextureBoundary extends Component<PlaqueTextureBoundaryProps, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

function PlaqueFrame({ item, selected, position, onSelect, children }: PlaqueVisualProps & { children?: ReactNode }) {
  function handlePointerDown(event: ThreeEvent<PointerEvent>) {
    event.stopPropagation();
    onSelect(item);
  }

  return (
    <group position={position} rotation={[0, item.wallAnchor.rotationY, 0]} onPointerDown={handlePointerDown}>
      <mesh position={[0, 0, -0.035]} castShadow receiveShadow>
        <boxGeometry args={[PLAQUE_WIDTH + 0.16, PLAQUE_HEIGHT + 0.16, 0.08]} />
        <meshStandardMaterial color={selected ? '#fbbf24' : '#172033'} roughness={0.42} metalness={0.18} />
      </mesh>
      {children}
      <Text
        position={[0, -0.98, 0.02]}
        maxWidth={PLAQUE_WIDTH + 0.25}
        fontSize={0.16}
        color="#f8fafc"
        anchorX="center"
        anchorY="middle"
      >
        {item.title}
      </Text>
      <Html position={[0, 1.02, 0.04]} center transform distanceFactor={10} zIndexRange={[20, 0]}>
        <div className="pointer-events-none whitespace-nowrap rounded-full border border-white/25 bg-slate-950/90 px-2 py-1 text-[10px] font-semibold text-white shadow-lg">
          {item.supportCount} {item.type === 'hunt_capture'
            ? item.supportCount === 1 ? 'finder' : 'finders'
            : item.supportCount === 1 ? 'supporter' : 'supporters'}
        </div>
      </Html>
    </group>
  );
}

function TexturedPlaque(props: PlaqueVisualProps) {
  const texture = useTexture(props.item.photoUrl ?? '');
  texture.colorSpace = 'srgb';

  return (
    <PlaqueFrame {...props}>
      <mesh position={[0, 0, 0.012]}>
        <planeGeometry args={[PLAQUE_WIDTH, PLAQUE_HEIGHT]} />
        <meshBasicMaterial map={texture} toneMapped={false} />
      </mesh>
    </PlaqueFrame>
  );
}

export function WallPlaqueMesh({ item, selected, wallTangentOffset = 0, onSelect }: WallPlaqueMeshProps) {
  const normalX = Math.sin(item.wallAnchor.rotationY);
  const normalZ = Math.cos(item.wallAnchor.rotationY);
  const position: [number, number, number] = [
    item.wallAnchor.x + normalX * WALL_OFFSET + Math.cos(item.wallAnchor.rotationY) * wallTangentOffset,
    item.wallAnchor.y,
    item.wallAnchor.z + normalZ * WALL_OFFSET - Math.sin(item.wallAnchor.rotationY) * wallTangentOffset,
  ];
  const props: PlaqueVisualProps = { item, selected, position, onSelect };

  return (
    <PlaqueTextureBoundary fallback={<PlaqueFrame {...props} />}>
      <Suspense fallback={<PlaqueFrame {...props} />}>
        <TexturedPlaque {...props} />
      </Suspense>
    </PlaqueTextureBoundary>
  );
}
